import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { MetasColaboradorService } from '../services/metasColaborador.service';
import type {
  CondicionPendiente,
  PropuestasCatalogo,
  PermisosPendiente,
  SuplentePendiente,
  TiendaRef,
} from '../types/metas.types';
import { bonosDe, faltaParaBodeguero, MARCAS_POR_PERSONA_DESDE } from '../utils/catalogo';
import { instanteMx } from '../utils/formato';
import { fechaCorta, numeroSemana, semanaEnFrase, sumarDias } from '../utils/semanas';
import { CondicionesResumen } from './CondicionesResumen';
import { CLASE_BOTON, CLASE_PANEL, cx } from './ui/clases';
import { ObjetivoResolucion, ResolverCatalogoDialog, tituloNoResuelta } from './ResolverCatalogoDialog';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

interface Props {
  onAviso: AvisoToast;
  /** La pestaña Autorización está a la vista: se recarga al volver a ella (el supervisor pudo proponer entre medias). */
  activa?: boolean;
  /** Sólo las de una tienda (desde «N de catálogo por resolver» de su fila). */
  tiendaFiltro?: TiendaRef | null;
  onQuitarFiltro?: () => void;
  /** Cuántas hay en total. */
  onTotal?: (n: number) => void;
  /** Se resolvió algo: la lista de tiendas y los contadores de la pestaña se recargan. */
  onCambio?: () => void;
  /**
   * Cambia cuando se propuso, retiró o resolvió algo desde la Captura: la
   * lista (y el total del título de la pestaña) se recarga aunque la pestaña
   * no esté a la vista.
   */
  recarga?: number;
  /**
   * «Seguimiento» de dirección (21-oct-2026): ve las propuestas de todas las
   * tiendas y no resuelve ninguna. Sin botones y sin el «no puedes resolverla
   * desde tu cuenta», que es para el regional que se topa con lo suyo.
   */
  soloLectura?: boolean;
}

const nombreTienda = (x: { chain: string; num: number; tienda: string | null }) =>
  `${x.chain} ${x.num}${x.tienda ? ` · ${x.tienda}` : ''}`;

const mismaTienda = (x: { chain: string; num: number }, t: TiendaRef | null | undefined) =>
  !t || (x.chain === t.chain && x.num === t.num);

/** «N° 18576 · Clave 0E3». */
const identidad = (s: SuplentePendiente) =>
  [s.numero !== null ? `N° ${s.numero}` : null, s.agnum ? `Clave ${s.agnum}` : null].filter(Boolean).join(' · ');

/** `id` de la sección: la fila de una tienda con propuestas enlaza aquí. */
export const ID_SECCION_PROPUESTAS = 'metas-propuestas';

/** El título de la sección; en «Seguimiento» (dirección) no hay nada que autorizar. */
export const TITULO_PROPUESTAS = 'Suplentes por autorizar';
export const TITULO_PROPUESTAS_SOLO_LECTURA = 'Suplentes propuestos';
/** …y mientras quede alguna condición de tienda propuesta (22-oct-2026). */
export const TITULO_PROPUESTAS_CON_CONDICIONES = 'Condiciones y suplentes por autorizar';
export const TITULO_PROPUESTAS_CON_CONDICIONES_SOLO_LECTURA = 'Condiciones y suplentes propuestos';

/** La última semana en que una condición autorizada paga por tienda (la S44). */
const ULTIMA_SEMANA_POR_TIENDA = sumarDias(MARCAS_POR_PERSONA_DESDE, -7);

/**
 * «Suplentes por autorizar», dentro de la pestaña Autorización (fase 3 del
 * rediseño; antes era la pestaña «Pendientes de catálogo», cuyo nombre pasa en
 * la fase 4 a la calidad de datos). Suplentes en desarrollo que esperan al
 * regional. No dependen de la semana elegida: cada propuesta dice desde cuándo
 * vale.
 *
 * 21-oct-2026 (decisión del usuario): sin «Condiciones de tienda». Transporte y
 * bodeguero se capturan POR PERSONA en cada semana.
 *
 * 22-oct-2026, propuesta de la sesión (ronda D), confirmada por el usuario el 23-oct a las 01:22 MX:
 * nadie PROPONE condiciones nuevas (la API responde 403), pero las que quedaron
 * propuestas —5 en prod, de la S43— se resuelven aquí, en «Condiciones por
 * resolver», y cuentan en el título y en el
 * total: sin esto se quedaban atoradas sin pantalla. La sección de condiciones
 * sólo sale mientras haya alguna; cada tarjeta dice qué paga DE VERDAD (difícil
 * acceso = Transporte a toda la tienda; bodeguero fijo sin bodega de más de un
 * piso = nada) y hasta cuándo: lo autorizado paga por tienda hasta la S44.
 *
 * PERMISOS (los da la API por propuesta, no se deducen aquí)
 * - `puedeResolver`: puede RECHAZAR.
 * - `puedeAutorizar`: además, puede autorizar. Es `false` cuando la propuesta
 *   quedó antes de la ventana (valdría en semanas ya cerradas): se dice por
 *   qué (`motivoNoAutorizar`) y lo que toca es rechazarla.
 * - Ninguno: la propuso él mismo, o no es regional de esa tienda.
 * - `soloLectura` (dirección, 21-oct-2026): la API le da las de TODAS las
 *   tiendas con `puedeResolver=false`. El paréntesis de «nadie resuelve lo que
 *   él mismo propuso» no le aplica (dirección nunca propone), así que se dice
 *   sólo quién la resuelve, y el título deja de decir «por autorizar».
 *
 * ⚠️ Cada decisión va por ID. Si el supervisor volvió a proponer, la
 * propuesta tiene id NUEVO y autorizar el viejo da 409: se recarga la lista y
 * se avisa, para que el regional lea lo nuevo antes de decidir.
 */
export const PropuestasCatalogoSeccion: React.FC<Props> = ({
  onAviso,
  activa,
  tiendaFiltro,
  onQuitarFiltro,
  onTotal,
  onCambio,
  recarga,
  soloLectura = false,
}) => {
  const [datos, setDatos] = useState<PropuestasCatalogo | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [objetivo, setObjetivo] = useState<ObjetivoResolucion | null>(null);

  // En una referencia y no en las dependencias de `cargar`: con un `onTotal`
  // nuevo en cada render, `cargar` cambiaría en cada render y, con la pestaña
  // activa, el efecto de abajo recargaría sin fin.
  const avisarTotal = useRef(onTotal);
  avisarTotal.current = onTotal;

  // Sólo cuenta la respuesta del ÚLTIMO pedido: la recarga que sigue a una
  // decisión no puede quedar pisada por la carga anterior que llegó tarde.
  const pedido = useRef(0);
  const cargar = useCallback(async () => {
    const n = ++pedido.current;
    setCargando(true);
    setError(null);
    try {
      const d = await MetasColaboradorService.propuestas();
      if (n !== pedido.current) return;
      setDatos(d);
      avisarTotal.current?.(d.condiciones.length + d.suplentes.length);
    } catch (e) {
      if (n !== pedido.current) return;
      setError((e as Error).message);
    } finally {
      if (n === pedido.current) setCargando(false);
    }
  }, []);

  // Al montar (para el total de la pestaña) y cada vez que se vuelve a ella.
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current || activa) {
      primera.current = false;
      void cargar();
    }
  }, [activa, cargar]);

  // Algo cambió desde la Captura: el total del título se quedaba con lo de
  // antes hasta volver a esta pestaña.
  useEffect(() => {
    if (recarga) void cargar();
  }, [recarga, cargar]);

  const condiciones = useMemo(
    () => (datos?.condiciones ?? []).filter((c) => mismaTienda(c, tiendaFiltro)),
    [datos, tiendaFiltro],
  );
  const suplentes = useMemo(
    () => (datos?.suplentes ?? []).filter((s) => mismaTienda(s, tiendaFiltro)),
    [datos, tiendaFiltro],
  );

  const alResolver = (mensaje: string) => {
    setObjetivo(null);
    onAviso('success', 'Listo', mensaje);
    void cargar();
    onCambio?.();
  };

  // Título neutro y el mensaje de la API: el 409 puede ser que la propuesta
  // cambió o la ventana (ver `tituloNoResuelta`).
  const alConflicto = (mensaje: string, accion: 'autorizar' | 'rechazar') => {
    setObjetivo(null);
    onAviso('warn', tituloNoResuelta(accion), `${mensaje} Se recargó la lista.`);
    void cargar();
    onCambio?.();
  };

  // ------------------------------------------------------------ piezas
  /**
   * Lo que se autoriza, con lo que paga de verdad: en prod, VAZ 57 pedía
   * «bodeguero fijo» sin «bodega de más de un piso» (no paga nada) y las de
   * difícil acceso nombraban a UNA persona (paga Transporte a toda la tienda).
   */
  const resumenCondicion = (c: CondicionPendiente) => {
    const bonos = bonosDe(c);
    const falta = faltaParaBodeguero(c);
    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm text-gray-700">
          Desde la <strong>{semanaEnFrase(c.vigenteDesde, true)}</strong>
        </div>
        <CondicionesResumen condicion={c} antes={c.vigenteActual ?? SIN_CONDICIONES} etiquetaAntes="hoy" />
        {bonos.transporte && (
          <div className="text-sm text-amber-800" data-testid="condicion-a-toda-la-tienda">
            Paga Transporte a todas las personas de la tienda, no sólo a quien nombre la justificación.
          </div>
        )}
        {falta && (
          <div className="text-sm text-amber-800" data-testid="condicion-sin-bono">
            {falta}
          </div>
        )}
        <div className="text-sm text-gray-700" style={{ overflowWrap: 'anywhere' }}>
          <span className="text-gray-600">Justificación: </span>«{c.justificacion}»
        </div>
      </div>
    );
  };

  const resumenSuplente = (s: SuplentePendiente) => (
    <div className="flex flex-col gap-1 text-sm text-gray-700">
      <div>
        <strong>{s.nombre}</strong>
        {identidad(s) && <span className="text-gray-500"> · {identidad(s)}</span>}
      </div>
      <div>
        En desarrollo desde el <strong>{fechaCorta(s.inicioDesarrollo)}</strong>
        {s.certificadoEn ? ` · certificado el ${fechaCorta(s.certificadoEn)}` : ' · sin certificar'}
      </div>
      <div className="text-xs text-gray-500">Bono semanal de suplente hasta certificarse, máximo 3 meses.</div>
    </div>
  );

  /** Botones según los permisos que manda la API para ESTA propuesta. */
  const acciones = (p: PermisosPendiente, abrir: (accion: 'autorizar' | 'rechazar') => void, nombre: string) => {
    if (soloLectura)
      return (
        <small className="text-gray-500" data-testid="sin-permiso">
          La resuelve el regional de la tienda.
        </small>
      );
    if (!p.puedeResolver)
      return (
        <small className="text-gray-500" data-testid="sin-permiso">
          No puedes resolverla desde tu cuenta: la resuelve el regional de la tienda (nadie resuelve lo que él mismo propuso).
        </small>
      );
    return (
      <div className="flex flex-col gap-2">
        {!p.puedeAutorizar && p.motivoNoAutorizar && <Message severity="warn" text={p.motivoNoAutorizar} />}
        <div className="flex flex-wrap gap-2">
          <Button
            label="Autorizar"
            icon="pi pi-check"
            severity="success"
            size="small"
            disabled={!p.puedeAutorizar}
            title={p.puedeAutorizar ? undefined : p.motivoNoAutorizar ?? 'No se puede autorizar'}
            onClick={() => abrir('autorizar')}
            aria-label={`Autorizar ${nombre}`}
          />
          <Button
            label="Rechazar…"
            icon="pi pi-times"
            severity="danger"
            outlined
            size="small"
            onClick={() => abrir('rechazar')}
            aria-label={`Rechazar ${nombre}`}
          />
        </div>
      </div>
    );
  };

  const cabecera = (x: { chain: string; num: number; tienda: string | null; supervisor: string | null }) => (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <strong className="text-gray-800">{nombreTienda(x)}</strong>
      {x.supervisor && <span className="text-xs text-gray-500">Supervisor {x.supervisor}</span>}
    </div>
  );

  // El nombre, como la lista y el historial; el correo queda de respaldo y en el title.
  const quien = (x: { propuestaPor: string; propuestaPorNombre?: string | null; propuestaEn: string }) => (
    <div className="text-xs text-gray-500" style={{ overflowWrap: 'anywhere' }}>
      Propuesta por <span title={x.propuestaPor}>{x.propuestaPorNombre || x.propuestaPor}</span> el {instanteMx(x.propuestaEn)}
    </div>
  );

  const totalCondiciones = datos?.condiciones.length ?? 0;
  const total = totalCondiciones + (datos?.suplentes.length ?? 0);
  const visibles = condiciones.length + suplentes.length;
  const titulo = totalCondiciones
    ? soloLectura
      ? TITULO_PROPUESTAS_CON_CONDICIONES_SOLO_LECTURA
      : TITULO_PROPUESTAS_CON_CONDICIONES
    : soloLectura
      ? TITULO_PROPUESTAS_SOLO_LECTURA
      : TITULO_PROPUESTAS;

  return (
    <section
      className={cx(CLASE_PANEL, 'flex flex-col gap-4 p-4 md:p-6')}
      data-testid="propuestas-catalogo"
      aria-labelledby={`${ID_SECCION_PROPUESTAS}-titulo`}
      id={ID_SECCION_PROPUESTAS}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={`${ID_SECCION_PROPUESTAS}-titulo`} tabIndex={-1} className="m-0 text-[15px] font-semibold text-metas-texto outline-none">
            {`${titulo}${datos ? ` · ${total}` : ''}`}
          </h2>
          <p className="m-0 max-w-[820px] text-[13px] text-metas-texto-2">
            Suplentes en desarrollo que proponen los supervisores. No dependen de la semana elegida: cada propuesta dice
            desde cuándo vale. Ya no se proponen condiciones de tienda: transporte y bodeguero se capturan por persona en
            cada semana.
            {totalCondiciones > 0 &&
              ` Las que quedaron propuestas se resuelven aquí: una autorizada paga por tienda hasta la semana ${numeroSemana(
                ULTIMA_SEMANA_POR_TIENDA,
              )} (la del ${fechaCorta(ULTIMA_SEMANA_POR_TIENDA)}); desde la ${numeroSemana(
                MARCAS_POR_PERSONA_DESDE,
              )}, sólo cuenta la marca de cada persona.`}
          </p>
        </div>
        <div className="flex-grow" />
        {tiendaFiltro && (
          <span className="inline-flex h-9 items-center gap-1 rounded-metas-pill border border-metas-borde-control bg-metas-cabecera px-3 text-[13px] font-semibold">
            Sólo {tiendaFiltro.chain} {tiendaFiltro.num}
            <button
              type="button"
              className="metas-foco ml-1 text-metas-texto-2"
              onClick={onQuitarFiltro}
              aria-label="Ver las de todas las tiendas"
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </span>
        )}
        <button type="button" className={CLASE_BOTON} onClick={() => void cargar()} disabled={cargando}>
          <i className={`pi ${cargando ? 'pi-spin pi-spinner' : 'pi-refresh'}`} aria-hidden="true" />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="flex flex-wrap items-center gap-2">
          <Message severity="error" text={`No se pudieron leer las propuestas: ${error}`} />
          <Button label="Reintentar" icon="pi pi-refresh" size="small" onClick={() => void cargar()} />
        </div>
      )}

      {datos && !visibles && (
        <div className="text-sm text-gray-500" data-testid="sin-pendientes">
          {tiendaFiltro && total
            ? 'Esa tienda ya no tiene propuestas esperando.'
            : totalCondiciones
              ? 'No hay condiciones ni suplentes esperando resolución.'
              : 'No hay suplentes esperando resolución.'}
        </div>
      )}

      {condiciones.length > 0 && (
        <section className="flex flex-col gap-2" data-testid="condiciones-por-resolver">
          <h3 className="m-0 text-sm font-semibold text-metas-texto">Condiciones por resolver ({condiciones.length})</h3>
          <ul className="flex flex-col gap-3">
            {condiciones.map((c) => {
              const tituloC = `Condiciones de ${nombreTienda(c)}`;
              return (
                <li
                  key={`c-${c.id}`}
                  className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3"
                  data-testid={`pendiente-condicion-${c.id}`}
                >
                  {cabecera(c)}
                  {resumenCondicion(c)}
                  {quien(c)}
                  {acciones(
                    c,
                    (accion) => setObjetivo({ tipo: 'condicion', id: c.id, accion, titulo: tituloC, resumen: resumenCondicion(c) }),
                    tituloC,
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {suplentes.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="m-0 text-sm font-semibold text-metas-texto">Suplentes en desarrollo ({suplentes.length})</h3>
          <ul className="flex flex-col gap-3">
            {suplentes.map((s) => {
              const titulo = `Suplente ${s.nombre} (${nombreTienda(s)})`;
              return (
                <li
                  key={`s-${s.id}`}
                  className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3"
                  data-testid={`pendiente-suplente-${s.id}`}
                >
                  {cabecera(s)}
                  {resumenSuplente(s)}
                  {quien(s)}
                  {acciones(
                    s,
                    (accion) => setObjetivo({ tipo: 'suplente', id: s.id, accion, titulo, resumen: resumenSuplente(s) }),
                    titulo,
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <ResolverCatalogoDialog
        objetivo={objetivo}
        onHide={() => setObjetivo(null)}
        onResuelto={alResolver}
        onConflicto={alConflicto}
      />
    </section>
  );
};

/** Una tienda sin versión autorizada: ninguna condición, ningún bono. */
const SIN_CONDICIONES = { dificilAcceso: false, bodegaMasDeUnPiso: false, bodegueroFijo: false };

export default PropuestasCatalogoSeccion;
